import type { ComponentType } from 'react'

import styled from 'styled-components'

import bankIcon from 'app/assets/icons/bank.svg'
import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { SectionMinorTitle as SectionMinorTitleBase } from 'app/components/display/text/standard_text/standard-body/component'
import { ContainerHeader as ContainerHeaderBase } from 'app/components/display/text/standard_text/standard-headings/component'
import { warn as logWarn } from 'app/services/snugLogger'
import { monthlyToWeekly } from 'app/utils/financial/conversions'
import {
  formatCurrency,
  formatISODate,
  formatNextPay,
  formatPercentage,
  getMissingEssentialsLabel,
} from 'app/utils/financial/formatters'

// ContainerHeader is a legacy .jsx component whose TS inference marks props as
// required when they have no default values, though all are truly optional at
// runtime. Cast to an all-optional interface so the call site only needs to
// pass the meaningful props.
const ContainerHeader = ContainerHeaderBase as ComponentType<{
  icon?: string
  text?: string
  textClass?: string
  isEditable?: boolean
  onEditClick?: () => void
  editTitle?: string
  componentClass?: string
  onNewNoteUpdated?: unknown
  newNotesContent?: unknown
  onNoteIconClicked?: unknown
  onNewNoteAdded?: unknown
  onNoteCrossClicked?: unknown
  interpreteNoteResult?: Record<string, unknown>
  displayedApplicant?: Record<string, unknown>
  section?: string
  enableNotesButton?: boolean
  editBtnClass?: string
  toggleRequestInfoModal?: unknown
  isManagerAdded?: boolean
  toggleEditRenterApplication?: unknown
  targetGUID?: unknown
  iconHeaderCmp?: unknown
}>

// SectionMinorTitle is a .jsx component; its TS inference treats `text` as
// required and other props as possibly-required. Cast so we can pass only
// `text` without synthetic undefined assignments.
const SectionMinorTitle = SectionMinorTitleBase as ComponentType<{
  text: string
  componentClass?: string
  textClass?: string
  imgSrc?: string
  imgAlt?: string
}>

// Row label constants — single source of truth for label strings so future
// copy changes apply once rather than hunting across component + tests.
const LABEL_WEEKLY_INCOME = 'Weekly income (after tax)'
const LABEL_MONTHLY_EQUIVALENT = 'Monthly equivalent'
const LABEL_MONTHLY_RENT = 'Monthly rent'
const LABEL_TOTAL_SAVINGS = 'Total savings'
const LABEL_RENT_TO_INCOME = 'Rent-to-income ratio'
const LABEL_VERIFIED_DATE = 'Verified date'
const LABEL_WEEKLY_SURPLUS = 'Weekly surplus after rent & commitments'
const LABEL_INCOME_STABILITY = 'Income stability'
const LABEL_INCOME_CONFIDENCE = 'Income confidence'
const LABEL_NEXT_PAY = 'Next pay'
const LABEL_MISSING_ESSENTIALS = 'Missing essentials'

/**
 * Resolves the weekly income figure for the renter panel.
 *
 * Precedence (mirrors the PM panel's verifiedWeekly hoist in F2b):
 *   1. `avgWeeklyIncome` when present (raw DP915 value from CreditSense).
 *   2. Approximate: `round((avgMonthlyIncome × 12) / 52)` when only monthly
 *      is available (pre-weekly-capture reports). Returns null when both absent.
 *
 * Weekly-only: no monthly secondary on renter side per design.
 */
function resolveRenterWeekly(
  verifiedIncome: RenterVerifiedIncome,
): number | null {
  if (verifiedIncome.avgWeeklyIncome != null) {
    return verifiedIncome.avgWeeklyIncome
  }
  if (verifiedIncome.avgMonthlyIncome != null) {
    return monthlyToWeekly(verifiedIncome.avgMonthlyIncome)
  }
  return null
}

// Bank-verified palette: matches comparison-indicator.match in
// manager_income_financials_container/style.scss. No shared SCSS variable
// exists for these greens — update both sites if the design token changes.
const BANK_VERIFIED_BG = '#e8f5e9'
const BANK_VERIFIED_FG = '#2e7d32'

const BankVerifiedBadge = styled.div.attrs({
  className: 'bank-verified-badge',
})`
  display: inline-flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 4px 10px;
  border-radius: 4px;
  background: ${BANK_VERIFIED_BG};
  color: ${BANK_VERIFIED_FG};
`
BankVerifiedBadge.displayName = 'BankVerifiedBadge'

const BankVerifiedPrimary = styled.span`
  font-size: 13px;
  font-weight: 600;
`
BankVerifiedPrimary.displayName = 'BankVerifiedPrimary'

const BankVerifiedSecondary = styled.span`
  font-size: 12px;
  font-weight: 400;
  opacity: 0.85;
`
BankVerifiedSecondary.displayName = 'BankVerifiedSecondary'

const MetricRow = styled.div.attrs({ className: 'metric-row' })`
  margin-bottom: 5px;
`
MetricRow.displayName = 'MetricRow'

// Bank/account block — mirrors `.fi-pm-bank-account` in the PM panel so the
// renter sees the same source-of-truth bank info as the PM. Update both sites
// if the design tokens change.
const BankAccount = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin: 8px 0 12px 0;
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
`
BankAccount.displayName = 'BankAccount'

const BankAccountIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`
BankAccountIcon.displayName = 'BankAccountIcon'

const BankAccountText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`
BankAccountText.displayName = 'BankAccountText'

const BankNameLine = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
`
BankNameLine.displayName = 'BankNameLine'

const AccountNameLine = styled.div`
  font-size: 13px;
  color: #555;
  line-height: 1.3;
`
AccountNameLine.displayName = 'AccountNameLine'

const MetricValue = styled.span.attrs<{ className?: string }>((props) => ({
  className: ['metric-value', props.className].filter(Boolean).join(' '),
}))`
  font-size: 14px;
`
MetricValue.displayName = 'MetricValue'

/**
 * AccountAccessed mirrors Go fi.AccountAccessed. BSB and account-number
 * suffixes are intentionally absent — PR28 (CU-86d2r1nfk) classified them
 * out-of-scope and CreditSense v2.3 does not return them.
 */
interface AccountAccessed {
  bankName?: string | null
  accountName?: string | null
}

interface RenterVerifiedIncome {
  isRedacted?: boolean
  avgMonthlyIncome?: number | null
  /** Mirrors Go fi.Report.AvgWeeklyIncome (json: avgWeeklyIncome). */
  avgWeeklyIncome?: number | null
  /** Mirrors Go fi.Report.BankName — display name of the primary bank. */
  bankName?: string | null
  /** Mirrors Go fi.Report.AccountsAccessed; bank/account block reads entry [0]. */
  accountsAccessed?: AccountAccessed[] | null
  avgMonthlyRent?: number | null
  totalSavings?: number | null
  /**
   * 0–1 fraction, e.g. 0.28 = 28%.
   * Displayed as `Math.round(value * 100)%` — if the API ever returns 0–100
   * instead, the display would be 100× wrong.
   */
  rentToIncomeRatio?: number | null
  reportCreatedAt?: string | null
  weeklySurplusAfterRentAndFinances?: number | null
  incomeStabilityDuration?: string | null
  incomeConfidence?: string | null
  nextPayDate?: string | null
  nextPayAmount?: number | null
  missingEssentialsGroceries?: boolean | null
  missingEssentialsTransport?: boolean | null
  missingEssentialsAccommodation?: boolean | null
}

interface Props {
  verifiedIncome?: RenterVerifiedIncome | null
  attachedToProfile: boolean
}

interface VerifiedMetricsProps {
  verifiedIncome: RenterVerifiedIncome
}

function VerifiedMetrics({ verifiedIncome }: VerifiedMetricsProps) {
  const formattedDate = formatISODate(verifiedIncome.reportCreatedAt)
  const nextPay = formatNextPay(
    verifiedIncome.nextPayDate,
    verifiedIncome.nextPayAmount,
  )
  const missingEssentials = getMissingEssentialsLabel(
    verifiedIncome.missingEssentialsGroceries,
    verifiedIncome.missingEssentialsTransport,
    verifiedIncome.missingEssentialsAccommodation,
  )

  // Weekly headline: DP915 raw weekly when available; approximate from monthly
  // when only legacy monthly data present (pre-weekly-capture report). Both
  // testids present when approx (additive, not XOR) per F3b plan.
  const weekly = resolveRenterWeekly(verifiedIncome)
  const isApprox =
    verifiedIncome.avgWeeklyIncome == null &&
    verifiedIncome.avgMonthlyIncome != null

  return (
    <div className="application-income-container">
      {weekly != null && (
        <MetricRow data-testid="fi-renter-weekly-income">
          <SectionMinorTitle text={LABEL_WEEKLY_INCOME} />
          <MetricValue
            className="fi-metric-value-primary"
            {...(isApprox
              ? {
                  'data-testid': 'fi-weekly-approximate',
                  title:
                    'Approximate — converted from monthly value (CreditSense report predates weekly capture).',
                }
              : {})}
          >
            {formatCurrency(weekly)}
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.avgMonthlyIncome != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_MONTHLY_EQUIVALENT} />
          <MetricValue>
            {formatCurrency(verifiedIncome.avgMonthlyIncome)}
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.avgMonthlyRent != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_MONTHLY_RENT} />
          <MetricValue>
            {formatCurrency(verifiedIncome.avgMonthlyRent)}
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.totalSavings != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_TOTAL_SAVINGS} />
          <MetricValue>
            {formatCurrency(verifiedIncome.totalSavings)}
          </MetricValue>
        </MetricRow>
      )}
      <MetricRow>
        <SectionMinorTitle text={LABEL_RENT_TO_INCOME} />
        <MetricValue>
          {formatPercentage(verifiedIncome.rentToIncomeRatio)}
        </MetricValue>
      </MetricRow>
      {formattedDate && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_VERIFIED_DATE} />
          <MetricValue>{formattedDate}</MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.weeklySurplusAfterRentAndFinances != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_WEEKLY_SURPLUS} />
          <MetricValue>
            {formatCurrency(verifiedIncome.weeklySurplusAfterRentAndFinances)}
            /wk
          </MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.incomeStabilityDuration != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_INCOME_STABILITY} />
          <MetricValue>{verifiedIncome.incomeStabilityDuration}</MetricValue>
        </MetricRow>
      )}
      {verifiedIncome.incomeConfidence != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_INCOME_CONFIDENCE} />
          <MetricValue>{verifiedIncome.incomeConfidence}</MetricValue>
        </MetricRow>
      )}
      {nextPay != null && (
        <MetricRow>
          <SectionMinorTitle text={LABEL_NEXT_PAY} />
          <MetricValue>{nextPay}</MetricValue>
        </MetricRow>
      )}
      <MetricRow>
        <SectionMinorTitle text={LABEL_MISSING_ESSENTIALS} />
        <MetricValue>{missingEssentials}</MetricValue>
      </MetricRow>
    </div>
  )
}

interface RenterBankAccountBlockProps {
  verifiedIncome: RenterVerifiedIncome
}

/**
 * Mirrors the PM .fi-pm-bank-account block so the renter sees the same
 * source-of-truth bank/account info as the PM. `bankName` falls back to the
 * primary AccountsAccessed entry when the top-level field hasn't been
 * populated yet (e.g. legacy reports written before BankName caching).
 */
function RenterBankAccountBlock({
  verifiedIncome,
}: RenterBankAccountBlockProps) {
  const primary =
    verifiedIncome.accountsAccessed &&
    verifiedIncome.accountsAccessed.length > 0
      ? verifiedIncome.accountsAccessed[0]
      : null
  const bankName = verifiedIncome.bankName || primary?.bankName || ''
  const accountName = primary?.accountName || ''
  if (!bankName && !accountName) return null

  return (
    <BankAccount data-testid="fi-renter-bank-account">
      <BankAccountIcon src={bankIcon} alt="" aria-hidden="true" />
      <BankAccountText>
        {bankName && (
          <BankNameLine data-testid="fi-renter-bank-name">
            {bankName}
          </BankNameLine>
        )}
        {accountName && (
          <AccountNameLine data-testid="fi-renter-account-name">
            {accountName}
          </AccountNameLine>
        )}
      </BankAccountText>
    </BankAccount>
  )
}

const RenterVerifiedFinancialsContainer = ({
  verifiedIncome,
  attachedToProfile,
}: Props) => {
  if (verifiedIncome?.isRedacted) {
    logWarn(
      '[RenterVerifiedFinancials] isRedacted unexpectedly true on renter view — backend should not redact own data',
    )
    return null
  }
  if (!verifiedIncome) return null
  if (!attachedToProfile) return null

  return (
    <div className="container-component renter-verified-financials-container bbs">
      <ContainerHeader
        icon={incomeIcon}
        text="Verified Financial Insights"
        isEditable={false}
        interpreteNoteResult={{}}
        section="verifiedFinancials"
      />
      <div className="application-container-padding">
        <BankVerifiedBadge aria-label="Bank Verified via Open Banking (CDR)">
          <BankVerifiedPrimary>Bank Verified</BankVerifiedPrimary>
          <BankVerifiedSecondary>via Open Banking (CDR)</BankVerifiedSecondary>
        </BankVerifiedBadge>
        <RenterBankAccountBlock verifiedIncome={verifiedIncome} />
        <VerifiedMetrics verifiedIncome={verifiedIncome} />
      </div>
    </div>
  )
}

export default RenterVerifiedFinancialsContainer
